import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

export interface KeywordTracking {
  id: string;
  site_id: string;
  keyword: string;
  search_engine: string;
  location: string;
  device: string;
  tracking_enabled: boolean;
  check_frequency: string;
  last_checked_at: string | null;
  created_at: string;
  current_position?: number | null;
  previous_position?: number | null;
}

export interface RankingData {
  id: string;
  keyword: string;
  search_engine: string;
  location: string;
  device: string;
  current_position: number | null;
  previous_position: number | null;
  position_change: number | null;
  has_featured_snippet: boolean;
  has_people_also_ask: boolean;
  has_knowledge_panel: boolean;
  snapshot_date: string;
}

export interface Opportunity {
  id: string;
  site_id: string;
  opportunity_type: string;
  keyword: string;
  question: string | null;
  search_volume: number | null;
  current_position: number | null;
  current_url: string | null;
  competitor_url: string | null;
  competitor_domain: string | null;
  competitor_position: number | null;
  recommended_content_type: string | null;
  recommended_word_count: number | null;
  recommended_elements: Record<string, any>;
  action_items: any[];
  impact_score: number | null;
  difficulty_score: number | null;
  priority_score: number | null;
  status: string;
  detected_at: string;
}

export interface Competitor {
  id: string;
  site_id: string;
  competitor_domain: string;
  competitor_name: string | null;
  discovered_via: string | null;
  keyword_overlap_count: number;
  shared_keywords: string[];
  content_similarity_score: number | null;
  created_at: string;
}

export interface SeoStats {
  total_keywords: number;
  ranked_keywords: number;
  top_3_rankings: number;
  top_10_rankings: number;
  average_position: number | null;
}

export interface RankingHistory {
  snapshot_date: string;
  position: number | null;
  has_featured_snippet: boolean;
  has_people_also_ask: boolean;
}

/**
 * Get all tracked keywords for a site
 */
export async function getKeywords(siteId: string): Promise<KeywordTracking[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/keywords`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch keywords');
  }

  return data.data;
}

/**
 * Add a new keyword to track
 */
export async function addKeyword(
  siteId: string,
  keyword: string,
  options?: {
    searchEngine?: string;
    location?: string;
    device?: string;
    checkFrequency?: string;
  }
): Promise<KeywordTracking> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/keywords`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      keyword,
      searchEngine: options?.searchEngine || 'google',
      location: options?.location || 'United States',
      device: options?.device || 'desktop',
      checkFrequency: options?.checkFrequency || 'daily'
    })
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to add keyword');
  }

  return data.data;
}

/**
 * Remove a keyword from tracking
 */
export async function removeKeyword(siteId: string, keywordId: string): Promise<void> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/keywords/${keywordId}`, {
    method: 'DELETE'
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to remove keyword');
  }
}

/**
 * Get current rankings for all tracked keywords
 */
export async function getRankings(siteId: string): Promise<RankingData[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/rankings`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch rankings');
  }

  return data.data;
}

/**
 * Get ranking history for a specific keyword
 */
export async function getRankingHistory(
  siteId: string,
  keywordId: string,
  days: number = 30
): Promise<RankingHistory[]> {
  const response = await authFetch(
    `${API_URL}/seo/sites/${siteId}/rankings/history/${keywordId}?days=${days}`
  );
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch ranking history');
  }

  return data.data;
}

/**
 * Get SEO opportunities for a site
 */
export async function getOpportunities(siteId: string): Promise<Opportunity[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/opportunities`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch opportunities');
  }

  return data.data;
}

/**
 * Trigger opportunity detection
 */
export async function detectOpportunities(siteId: string): Promise<Opportunity[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/opportunities/detect`, {
    method: 'POST'
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to detect opportunities');
  }

  return data.data;
}

/**
 * Get competitors for a site
 */
export async function getCompetitors(siteId: string): Promise<Competitor[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/competitors`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch competitors');
  }

  return data.data;
}

/**
 * Get SEO statistics for a site
 */
export async function getSeoStats(siteId: string): Promise<SeoStats> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/seo-stats`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch SEO stats');
  }

  return data.data;
}

/**
 * Manually trigger tracking for a specific keyword
 */
export async function trackKeyword(siteId: string, keywordId: string): Promise<any> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/keywords/${keywordId}/track`, {
    method: 'POST'
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to track keyword');
  }

  return data.data;
}

export interface KeywordSuggestion {
  keyword: string;
  intent: 'commercial' | 'informational' | 'transactional' | 'navigational' | 'competitive';
  difficulty: 'easy' | 'medium' | 'hard';
  reason: string;
  source: string;
  score: number;
  alreadyTracked?: boolean;
}

/**
 * Get AI-powered keyword suggestions for a site
 */
export async function getKeywordSuggestions(siteId: string, limit: number = 20): Promise<KeywordSuggestion[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/keyword-suggestions?limit=${limit}`);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get keyword suggestions');
  }

  return data.data;
}

export interface KeywordDiscovery {
  keyword: string;
  position: number;
  source: string;
  url: string;
  title: string;
  hasFeaturedSnippet: boolean;
  hasPeopleAlsoAsk: boolean;
  testDate: string;
}

/**
 * Discover keywords the site actually ranks for
 */
export async function discoverKeywords(siteId: string, maxKeywordsToTest: number = 50): Promise<KeywordDiscovery[]> {
  const response = await authFetch(`${API_URL}/seo/sites/${siteId}/discover-keywords`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      limit: 20,
      maxKeywordsToTest
    })
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to discover keywords');
  }

  return data.data;
}
